import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { log, LogLevel } from "utils/LogUtil";

// 개발 환경: 프록시 사용 (빈 문자열)
// 프로덕션 환경: 환경변수 사용
const BASE_URL =
  process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL || "" : "";

// 토큰 갱신 상태 관리
let isRefreshing = false;
let failedQueue: Array<{ resolve: () => void; reject: (error: AxiosError) => void }> = [];

const processQueue = (error: AxiosError | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve()));
  failedQueue = [];
};

// axios 인스턴스 생성
const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000, // 10초
  headers: {
    "Content-Type": "application/json",
  },
  // 쿠키를 요청에 포함시키기 위한 설정
  withCredentials: true,
});

// 요청 인터셉터
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 요청 시 인증 정보 확인 로깅 (디버깅용)
    if (process.env.NODE_ENV !== "production") {
      log(LogLevel.VERBOSE, "Request cookies included:", document.cookie.includes("prai-id-token"));
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 인증 에러 (401) 발생 시 토큰 갱신 시도
    if (error.response?.status === 401) {
      log(LogLevel.VERBOSE, "인증 에러 발생. 현재 경로:", window.location.pathname);

      const currentPath = window.location.pathname;
      const publicPaths = [
        "/login",
        "/",
        "/signup/ymyd",
        "/agreements",
        "/auth/kakao/callback", // 카카오 OAuth 콜백 (회원가입 전 비인증 진입 동선)
      ];
      // 케어링노트 파트너 열람(/caringnote/embed·report)은 URL 토큰이 유일한 인증이다.
      // 3단 뷰어가 부르는 인증 API(getPillsByItemCodes 등)가 401이어도 로그인으로 튕기면 안 된다
      // (계획 20260814-caringnote-report-3pane §0-ⓓ R-6). startsWith로 두 경로를 모두 공개 처리.
      const isPublicPath =
        publicPaths.includes(currentPath) ||
        currentPath.startsWith('/nhis/') ||
        currentPath.startsWith('/caringnote/');

      // refresh 요청 자체 실패 또는 재시도 실패 → 로그인으로
      if (originalRequest?.url === "/api/v1/auth/refresh" || originalRequest?._retry) {
        isRefreshing = false;
        processQueue(error);
        if (!isPublicPath) {
          localStorage.setItem("authRedirect", currentPath);
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      // refresh 진행 중 → 큐에서 대기
      if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          originalRequest._retry = true;
          return axiosInstance(originalRequest);
        });
      }

      // refresh 시도
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axiosInstance.post("/api/v1/auth/refresh");
        log(LogLevel.VERBOSE, "토큰 갱신 성공");
        processQueue(null);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        log(LogLevel.VERBOSE, "토큰 갱신 실패. 로그인 필요.");
        processQueue(refreshError as AxiosError);
        if (!isPublicPath) {
          localStorage.setItem("authRedirect", currentPath);
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
